import {formatDate, Options} from '@fl/cmsch-fe-library';

import {ApcDate} from 'api/gen/ApcDate';
import {ApcDateListWithStables} from 'api/gen/ApcDateListWithStables';
import {ApcStable} from 'api/gen/ApcStable';
import {ApcStableListWithDates} from 'api/gen/ApcStableListWithDates';
import {Date} from 'api/gen/Date';

/**
 * Creates stable options for a protocol-backed date selection.
 *
 * @param relations Stable/date relations returned for a protocol-backed ear tag.
 * @returns A mapper that turns the selected date into stable select options.
 */
export const genProtocolStableOptionsBasedOnDate = (relations: ApcDateListWithStables) =>
    (date: Date): Options<string> => relations.dateData
        ?.find(data => data.proficiencyTestDate === date)
        ?.stables
        ?.map(stable => ({
            label: stable.code,
            value: stable.code,
        })) || [];

/**
 * Creates the full stable option list from protocol-backed stable relations.
 *
 * @param relations Stable/date relations returned for a protocol-backed ear tag.
 * @returns Stable options for the APC stable select.
 */
export const genProtocolStableOptionsBasedOnStable = (relations: ApcDateListWithStables): Options<string> =>
    relations.stableData
        ?.map(stables => ({
            label: stables.stable.code,
            value: stables.stable.code,
        })) || [];

/**
 * Creates date options for a selected stable when protocol relations are available.
 *
 * @param relations Stable/date relations returned for a protocol-backed ear tag.
 * @returns A mapper that turns the selected stable into proficiency-test date options.
 */
export const genProtocolDateOptionsBasedOnStable = (relations: ApcDateListWithStables) =>
    (stableName: string): Options<string> =>
        relations.stableData
            ?.find(data => data.stable.code === stableName)
            ?.proficiencyTestDates
            ?.map(date => ({
                label: formatDate(date).orElse(''),
                value: date,
            })) || [];

/**
 * Creates date options for a selected stable when only non-protocol relations are available.
 *
 * @param relations Stable/date relations returned for a stable-only lookup.
 * @returns A mapper that turns the selected stable into proficiency-test date options.
 */
export const genNonProtocolDateOptionsBasedOnStable = (relations: ApcStableListWithDates) =>
    (stableName: string): Options<string> =>
        relations.data
            ?.find(data => data.stable.code === stableName)
            ?.proficiencyTestDates
            ?.map(date => ({
                label: formatDate(date).orElse(''),
                value: date,
            })) || [];

/**
 * Converts APC date relation rows into select options.
 *
 * @param apcDateList Date rows returned by the APC relations endpoint.
 * @returns Date select options for the proficiency-test date field.
 */
export const getDateOptions = (apcDateList: Array<ApcDate> | null): Options<string> =>
    apcDateList?.map(data => ({
        label: formatDate(data.proficiencyTestDate).orElse(''),
        value: data.proficiencyTestDate,
    })) || [];

/**
 * Converts APC stable relation rows into select options.
 *
 * @param apcStableList Stable rows returned by the APC relations endpoint.
 * @returns Stable select options for the stable field.
 */
export const getStableOptions = (apcStableList: Array<ApcStable> | null): Options<string> =>
    apcStableList?.map(data => ({
        label: data.stable.code,
        value: data.stable.code,
    })) || [];
